import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Select,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconFilter,
  IconRefresh,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  useProceduresAdmin,
  useCreateProcedure,
  useUpdateProcedure,
  useToggleProcedureActive,
} from "../../../hooks/admin/useProceduresAdmin";
import ProcedureForm, {
  ProcedureFormValues,
} from "../../../components/admin/procedures/ProcedureForm";
import { Procedure } from "../../../services/admin/procedures.service";

interface ProcedureFilters {
  status?: "all" | "active" | "inactive";
}

const ITEMS_PER_PAGE = 20;

export default function ProceduresAdminPage() {
  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editing, setEditing] = useState<Procedure | null>(null);
  const [itemToToggle, setItemToToggle] = useState<Procedure | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<ProcedureFilters>({ status: "all" });
  const [pendingFilters, setPendingFilters] = useState<ProcedureFilters>({
    status: "all",
  });
  const [page, setPage] = useState(1);

  // Data hooks
  const { data: procedures = [], isLoading, refetch } = useProceduresAdmin();
  const createProcedure = useCreateProcedure();
  const updateProcedure = useUpdateProcedure();
  const toggleProcedureActive = useToggleProcedureActive();

  // Filter data
  const filteredData = useMemo(() => {
    let result = procedures;

    // Search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(searchLower) ||
          (item.description &&
            item.description.toLowerCase().includes(searchLower))
      );
    }

    // Status filter
    if (filters.status === "active") {
      result = result.filter((item) => item.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((item) => !item.isActive);
    }

    return result;
  }, [procedures, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  // Check if any filters are active (excluding "all" status)
  const hasActiveFilters = filters.status !== "all";

  // Handlers
  const handleAdd = () => {
    setEditing(null);
    openFormModal();
  };

  const handleEdit = (item: Procedure) => {
    setEditing(item);
    openFormModal();
  };

  const handleSubmit = async (values: ProcedureFormValues) => {
    if (editing) {
      await updateProcedure.mutateAsync({
        id: editing.id,
        ...values,
      });
    } else {
      await createProcedure.mutateAsync(values);
    }
    closeFormModal();
  };

  const handleToggleActive = (item: Procedure) => {
    setItemToToggle(item);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (itemToToggle) {
      await toggleProcedureActive.mutateAsync({
        id: itemToToggle.id,
        isActive: !itemToToggle.isActive,
      });
      closeConfirmModal();
      setItemToToggle(null);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({ status: "all" });
    setPendingFilters({ status: "all" });
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({ status: "all" });
  };

  // Status options for filter
  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "active", label: "Active Only" },
    { value: "inactive", label: "Inactive Only" },
  ];

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Procedures</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage medical procedures and examination types
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status === "active" && (
                      <Badge variant="light" color="green">
                        Active Only
                      </Badge>
                    )}
                    {filters.status === "inactive" && (
                      <Badge variant="light" color="red">
                        Inactive Only
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} procedure
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Procedures ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No procedures match the current filters"
                    : "No procedures found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Procedure Name</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr
                        key={item.id}
                        style={{ opacity: item.isActive ? 1 : 0.5 }}
                      >
                        {/* Name */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.name}
                          </Text>
                        </Table.Td>

                        {/* Description */}
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {item.description || "—"}
                          </Text>
                        </Table.Td>

                        {/* Created */}
                        <Table.Td>
                          <Text size="sm">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </Text>
                        </Table.Td>

                        {/* Status */}
                        <Table.Td>
                          <Badge
                            color={item.isActive ? "green" : "red"}
                            variant="light"
                          >
                            {item.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={item.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={item.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(item)}
                              >
                                {item.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editing ? "Edit Procedure" : "Add New Procedure"}
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <ProcedureForm
              initialValues={editing || undefined}
              onSubmit={handleSubmit}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createProcedure.isPending || updateProcedure.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editing ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Procedures"
          size="sm"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="Select status..."
              data={statusOptions}
              value={pendingFilters.status || "all"}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: (value as ProcedureFilters["status"]) || "all",
                })
              }
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Toggle Active Confirmation Modal */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            itemToToggle?.isActive
              ? "Deactivate Procedure"
              : "Activate Procedure"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {itemToToggle?.isActive ? "deactivate" : "activate"}{" "}
              <strong>{itemToToggle?.name}</strong>?
            </Text>
            {itemToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated procedures will not appear in dropdown selections
                for new cases.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={itemToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleProcedureActive.isPending}
              >
                {itemToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
